import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, LogOut, Users, Mail, Send, RefreshCw, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'moonshine_admin_token';

function Login({ onLogin }) {
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Login fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md p-8 rounded-2xl border border-[#d4b574]/15 bg-[#0e0e0c]">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-11 h-11 rounded-full border border-[#d4b574]/30 flex items-center justify-center text-[#e8d5a8]">
            <Lock size={18} />
          </div>
          <div>
            <h1 className="font-serif-display text-2xl text-[#f5efe3]">Admin-Bereich</h1>
            <p className="text-xs text-[#7a7060]">Moonshine Solutions</p>
          </div>
        </div>
        <label className="text-xs uppercase tracking-[0.22em] text-[#9a9285]">Passwort</label>
        <input
          type="password"
          value={pw}
          autoFocus
          onChange={(e) => setPw(e.target.value)}
          className="mt-2 w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 transition"
        />
        {err && <p className="mt-3 text-xs text-red-400">{err}</p>}
        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors disabled:opacity-60"
        >
          {loading ? 'Anmelden…' : 'Anmelden'} <ArrowRight size={16} />
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bcSubject, setBcSubject] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcPre, setBcPre] = useState('');
  const [bcMsg, setBcMsg] = useState('');
  const [bcSending, setBcSending] = useState(false);
  const [schedMsg, setSchedMsg] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { headers });
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScheduler = async () => {
    setSchedMsg('Läuft…');
    try {
      await axios.post(`${API}/admin/run-scheduler`, {}, { headers });
      setSchedMsg('Scheduler ausgeführt ✓');
      load();
    } catch (e) {
      setSchedMsg('Fehler beim Ausführen');
    }
    setTimeout(() => setSchedMsg(''), 4000);
  };

  const broadcast = async (e) => {
    e.preventDefault();
    if (!bcSubject || !bcBody) return;
    if (!window.confirm(`Wirklich an alle aktiven Abonnenten senden? (${stats?.subscribers_active || 0})`)) return;
    setBcSending(true);
    setBcMsg('');
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast`,
        { subject: bcSubject, html_body: bcBody, preheader: bcPre },
        { headers },
      );
      setBcMsg(`Gesendet: ${data.sent} · Fehler: ${data.failed} · Total: ${data.total}`);
      setBcSubject(''); setBcBody(''); setBcPre('');
    } catch (e) {
      setBcMsg(e?.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setBcSending(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#9a9285]">Lade Daten…</div>
    );
  }

  return (
    <div className="min-h-screen px-6 lg:px-10 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-2">Admin-Bereich</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">Newsletter Dashboard</h1>
          </div>
          <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40 hover:text-[#e8d5a8] text-sm transition-all">
            <LogOut size={14} /> Abmelden
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Abonnenten aktiv', value: stats.subscribers_active, icon: Users, accent: '#9ce7a8' },
            { label: 'Abgemeldet', value: stats.subscribers_unsubscribed, icon: AlertTriangle, accent: '#e8b4a8' },
            { label: 'Total Anmeldungen', value: stats.subscribers_total, icon: Mail, accent: '#e8d5a8' },
            { label: 'Kontaktanfragen', value: stats.contact_messages, icon: Mail, accent: '#d4b574' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-5">
                <Icon size={18} style={{ color: s.accent }} />
                <div className="font-serif-display text-3xl mt-3" style={{ color: s.accent }}>{s.value}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#9a9285] mt-1">{s.label}</div>
              </div>
            );
          })}
        </div>

        {/* Drip Schedule */}
        <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-7 mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-serif-display text-2xl text-[#f5efe3]">Drip-Sequenz</h2>
              <p className="text-sm text-[#9a9285]">Automatischer Versand läuft stündlich</p>
            </div>
            <button onClick={runScheduler} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4b574]/40 text-[#e8d5a8] hover:bg-[#d4b574]/10 text-sm transition-all">
              <RefreshCw size={14} /> Jetzt prüfen
            </button>
          </div>
          {schedMsg && <div className="mb-4 text-xs text-[#d4b574]">{schedMsg}</div>}
          <div className="grid md:grid-cols-3 gap-3">
            {stats.drip_schedule.map((d) => {
              const dist = stats.sequence_distribution.find((x) => x.sequence === d.seq);
              return (
                <div key={d.seq} className="px-4 py-3 rounded-xl border border-white/8 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a7060]">Mail #{d.seq} · Tag {d.delay_days}</div>
                    <div className="text-sm text-[#e8d5a8] capitalize">{d.key}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[#9a9285]">aktuell</div>
                    <div className="font-serif-display text-lg text-[#f5efe3]">{dist?.count ?? 0}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Broadcast */}
        <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-7 mb-10">
          <h2 className="font-serif-display text-2xl text-[#f5efe3]">Newsletter manuell senden</h2>
          <p className="text-sm text-[#9a9285] mb-6">An alle aktiven Abonnenten ({stats.subscribers_active}). HTML wird unterstützt.</p>
          <form onSubmit={broadcast} className="space-y-3">
            <input
              placeholder="Betreff"
              value={bcSubject}
              onChange={(e) => setBcSubject(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60"
            />
            <input
              placeholder="Preheader (kurze Vorschau im Postfach)"
              value={bcPre}
              onChange={(e) => setBcPre(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60"
            />
            <textarea
              rows={10}
              placeholder='HTML-Body, z.B. <h1>Titel</h1><p>Text…</p>'
              value={bcBody}
              onChange={(e) => setBcBody(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-[#f5efe3] focus:outline-none focus:border-[#d4b574]/60 font-mono text-sm resize-none"
            />
            <button
              type="submit"
              disabled={bcSending || !bcSubject || !bcBody}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors disabled:opacity-50"
            >
              {bcSending ? 'Wird gesendet…' : 'An alle senden'} <Send size={14} />
            </button>
            {bcMsg && (
              <div className="mt-3 px-4 py-3 rounded-lg bg-[#d4b574]/10 border border-[#d4b574]/25 text-sm text-[#e8d5a8] flex items-center gap-2">
                <CheckCircle2 size={16} /> {bcMsg}
              </div>
            )}
          </form>
        </div>

        {/* Recent subscribers */}
        <div className="rounded-2xl border border-white/8 bg-[#0e0e0c] p-7">
          <h2 className="font-serif-display text-2xl text-[#f5efe3] mb-1">Neueste Anmeldungen</h2>
          <p className="text-sm text-[#9a9285] mb-5">Die letzten 20 Einträge</p>
          <div className="divide-y divide-white/5">
            {stats.recent_subscribers.map((s, i) => (
              <div key={i} className="py-3 flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-[#f5efe3] truncate">{s.firstname} {s.lastname}</div>
                  <div className="text-xs text-[#7a7060] truncate">{s.email}</div>
                </div>
                <div className="text-xs text-[#9a9285] whitespace-nowrap">
                  Mail {s.last_sent_sequence || 0}/9
                </div>
                <div className="text-xs text-[#7a7060] whitespace-nowrap">
                  {s.created_at?.slice(0, 10)}
                </div>
                {s.unsubscribed_at && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-red-400">abgemeldet</span>
                )}
              </div>
            ))}
            {stats.recent_subscribers.length === 0 && (
              <div className="py-6 text-sm text-[#7a7060]">Noch keine Anmeldungen.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      <SEO path="/admin" title="Admin" description="Admin-Bereich" noindex />
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </>
  );
}
